import pipe from 'lodash/fp/pipe';
import { parseSearch as parser } from './url';
import { INCOMPLETE_STATUSES, COMPLETE_STATUSES } from '../const/status';

export const SEARCH_TYPE = {
  process: 'processes',
  run: 'runs',
  table: 'tables',
  task: 'tasks'
};

// Pending api support
export const SEARCH_STATUS = {
  incomplete: INCOMPLETE_STATUSES.join(','),
  complete: COMPLETE_STATUSES.join(',')
};

export const UNIVERSAL_SEARCH_OPERATORS = [
  'assignee',
  'category',
  'is',
  'owner',
  'pageKey',
  'participatingUsers',
  'sort',
  'type',
  ...Object.keys(SEARCH_TYPE)
];

/**
 * Helper functions
 */

const joinIfArray = (v, separator = ',') =>
  Array.isArray(v) ? v.join(separator) : v;

// Creates a function that takes an object and renames a key when the key is present
// Performs Array.join when value is an array
const aliasOrJoinProperty = (key, newKey = key) => obj => {
  const { [key]: value, ...rest } = obj;
  if (value === undefined) {
    return obj;
  } else {
    return {
      ...rest,
      [newKey]: joinIfArray(value)
    };
  }
};

export const omitSurroundingQuotes = s => {
  const last = s.length - 1;
  return s.charAt(0) === '"' && s.charAt(last) === '"' ? s.slice(1, last) : s;
};

/**
 * Search transform functions
 */

const transformSort = ({ sort, ...search }) => {
  if (sort) {
    return {
      ...search,
      sort: joinIfArray(sort).replace(/-/g, ':')
    };
  } else {
    return search;
  }
};

const transformExplicitType = ({ type: typeProp, ...search }) => {
  if (typeProp) {
    return {
      ...search,
      types: typeProp.includes(',')
        ? typeProp
            .split(',')
            .map(type => SEARCH_TYPE[type])
            .join(',')
        : SEARCH_TYPE[typeProp]
    };
  } else {
    return search;
  }
};

const transformImplicitType = search =>
  Object.keys(SEARCH_TYPE).reduce((search, type) => {
    if (search.hasOwnProperty(type)) {
      const { [type]: typeText, ...rest } = search;
      return {
        ...rest,
        types: [search.types, SEARCH_TYPE[type]].filter(Boolean).join(','),
        q: [search.q, typeText].filter(Boolean).join(' ')
      };
    } else {
      return search;
    }
  }, search);

const transformText = ({ text: textProp, ...search }) => {
  if (textProp) {
    const text =
      search.type || search.types ? omitSurroundingQuotes(textProp) : textProp;
    return {
      ...search,
      q: [search.q, text].filter(Boolean).join(' ')
    };
  } else {
    return search;
  }
};

const transformType = pipe(
  transformExplicitType,
  transformImplicitType
);
const transformAssignee = aliasOrJoinProperty('assignee', 'assignedTo');
const transformIs = aliasOrJoinProperty('is', 'status');
const transformPageKey = aliasOrJoinProperty('pageKey', 'pageToken');
const transformOwner = aliasOrJoinProperty('owner');
const omitUnneededProps = ({ exclude, offsets, ...search }) => search;
const maybeUseArgument = o => (o == null ? {} : o);

export const transformSearch = pipe(
  maybeUseArgument,
  omitUnneededProps,
  transformPageKey,
  transformIs,
  transformAssignee,
  transformOwner,
  transformType,
  transformSort,
  transformText
);

const parseSearch = search =>
  parser(search, { keywords: UNIVERSAL_SEARCH_OPERATORS });

export const transformSearchQuery = pipe(
  parseSearch,
  transformSearch
);



// WEBPACK FOOTER //
// ./js/util/search.js